import React, { Ref, forwardRef, CSSProperties, useMemo, ReactNode, useEffect, useState } from 'react';
import { classNames } from 'primereact/utils';
import { universalRenderer } from 'utils/universalRenderer';
import { nameInitialsSplitter } from 'utils/nameInitialsSplitter';
import { getAvatarColor } from 'utils/getAvatarColor';
import './Avatar.scss';
import { Icon } from '../Icon';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type AvatarProps = {
	id?: number | string | null;
	src?: string | null;
	size?: AvatarSize;
	name: string | null;
	render?: ReactNode | (() => ReactNode);
	containerClassName?: string;
	className?: string;
	style?: CSSProperties;
};

const formatAvatarSrc = (url?: string | null): string | null => {
	if (!url) return null;
	if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('/')) {
		return url;
	}
	return `/${url}`;
};

export const Avatar = forwardRef(
	(
		{ id, src, size = 'md', name, render, className, containerClassName, style, ...restProps }: AvatarProps,
		ref: Ref<HTMLDivElement>
	) => {
		const finalContainerClassName = useMemo(
			() => classNames('avatar-container', containerClassName),
			[containerClassName]
		);
		const initials = name ? nameInitialsSplitter(name) : null;
		const normalizedSrc = useMemo(() => formatAvatarSrc(src), [src]);

		const [hasImgError, setHasImgError] = useState(false);

		useEffect(() => {
			setHasImgError(false);
		}, [normalizedSrc]);

		const showImage = Boolean(normalizedSrc && !hasImgError);

		const avatarBgColor = useMemo(() => {
			if (!showImage) {
				const seed = id !== undefined && id !== null ? id : name || initials;
				return getAvatarColor(seed);
			}
			return undefined;
		}, [showImage, id, name, initials]);

		const avatarStyle = useMemo(
			() => (avatarBgColor ? { backgroundColor: avatarBgColor, ...style } : style),
			[avatarBgColor, style]
		);

		const finalAvatarClassName = useMemo(
			() => classNames('avatar', `avatar-size-${size}`, className),
			[size, className]
		);

		return (
			<div ref={ref} className={finalContainerClassName} {...restProps}>
				<div className={finalAvatarClassName} style={avatarStyle}>
					{!showImage && (
						// eslint-disable-next-line react/jsx-no-useless-fragment
						<>
							{initials === null ? (
								<div className="avatar-initials">
									<Icon name="user" iconStyle="regular" />
								</div>
							) : (
								<div
									className="avatar-initials"
									aria-label={name || ''}
									role="img"
									title={initials || ''}
								>
									{initials}
								</div>
							)}
						</>
					)}
					{showImage && (
						<div className="avatar-image" style={{ backgroundImage: `url("${normalizedSrc}")` }} role="img">
							<img
								src={normalizedSrc!}
								alt=""
								style={{ display: 'none' }}
								onError={() => setHasImgError(true)}
							/>
						</div>
					)}
				</div>
				{render && universalRenderer(render)}
			</div>
		);
	}
);
